import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createDraftOrder, listPartnerVenues, getVenueRoomAvailability } from '../../services/marketplaceOrderService';
import { checkZoneCoverage } from '../../services/zoneService';
import { listServices } from '../../services/serviceService';
import { useAuth } from '../../context/AuthContext';
import ZonePicker from '../../Components/shared/ZonePicker';
import SEO from '../../Components/shared/SEO';

const HEALTH_CONDITIONS = [
  'Alcohol within 24hrs', 'Recent Surgery', 'Infection', 'Kidney alignment',
  'Open wounds', 'Emotional changes', 'Phlebitis', 'Chronic pains',
  'Sinus congestion', 'Bruises', 'Blood clot', 'Headaches',
  'High Blood pressure', 'Fever within 24hrs', 'Wear contacts', 'Flu',
  'Acute pain', 'Grief process', 'Allergies', 'Arthritis',
  'Diabetes', 'Varicose Veins', 'Skin Conditions', 'Recent Injuries',
  'Pregnancy', 'Osteoporosis', 'Cancer', 'Heart Condition'
];

const REFERRAL_SOURCES = [
  'Social Media', 'Word of Mouth', 'Google Search', 'Flyer/Poster',
  'Friend/Family Recommendation', 'Other'
];

const STEPS = ['Massage', 'Location & Time', 'Health & Safety', 'Review & Pay'];

const initialForm = {
  massageType: '',
  venueType: 'client_location',
  venueId: '',
  roomId: '',
  slot: '',
  address: '',
  zone: '',
  notes: '',
  scheduledDate: '',
  scheduledTime: '',
  referralSource: '',
  previousMassageTherapy: false,
  onMedication: false,
  medicationDetails: '',
  exercising: false,
  exerciseFrequency: '',
  exerciseHours: '',
  healthConditions: [],
  disclaimerAgreed: false
};

const BookMassagePage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [services, setServices] = useState([]);
  const [venues, setVenues] = useState([]);
  const [slots, setSlots] = useState([]);
  const [coverage, setCoverage] = useState(null); // null = not checked, {covered, therapistCount}
  const [checkingCoverage, setCheckingCoverage] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listServices().then((list) => {
      setServices(list);
      setForm((prev) => ({ ...prev, massageType: prev.massageType || list[0]?.name || '' }));
    });
    listPartnerVenues().then(setVenues);
  }, []);

  useEffect(() => {
    if (form.venueType === 'partner_venue' && form.venueId && form.roomId) {
      getVenueRoomAvailability(form.venueId, form.roomId).then(setSlots);
    } else {
      setSlots([]);
    }
  }, [form.venueType, form.venueId, form.roomId]);

  // Let the client know upfront whether anyone actually covers their zone,
  // rather than finding out only after paying and waiting for a match.
  useEffect(() => {
    if (form.venueType !== 'client_location' || !form.zone) {
      setCoverage(null);
      return;
    }
    setCheckingCoverage(true);
    checkZoneCoverage(form.zone)
      .then(setCoverage)
      .catch(() => setCoverage(null))
      .finally(() => setCheckingCoverage(false));
  }, [form.venueType, form.zone]);

  const selectedMassage = services.find(m => m.name === form.massageType);
  const selectedVenue = venues.find(v => v._id === form.venueId);
  const selectedRoom = selectedVenue?.rooms.find(r => r._id === form.roomId);
  const totalAmount = (selectedMassage?.price || 0) + (selectedRoom?.pricePerSession || 0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleHealthCondition = (condition) => {
    setForm(prev => ({
      ...prev,
      healthConditions: prev.healthConditions.includes(condition)
        ? prev.healthConditions.filter(c => c !== condition)
        : [...prev.healthConditions, condition]
    }));
  };

  const stepError = () => {
    if (step === 0) {
      if (!form.massageType) return 'Please select a massage type';
    }
    if (step === 1) {
      if (form.venueType === 'client_location') {
        if (!form.address || !form.zone || !form.scheduledDate || !form.scheduledTime) {
          return 'Please fill in your address, zone, date and time';
        }
        if (checkingCoverage) {
          return 'Please wait while we check therapist availability in your zone';
        }
        if (coverage && !coverage.covered) {
          return 'We don\'t have a verified therapist covering this zone yet, so we can\'t take this booking. Please choose a different zone, or a partner venue.';
        }
      } else {
        if (!form.venueId || !form.roomId || !form.slot) {
          return 'Please select a venue, room and time slot';
        }
      }
    }
    if (step === 3) {
      if (!form.disclaimerAgreed) return 'You must agree to the health disclaimer to continue';
    }
    return '';
  };

  const goNext = () => {
    const err = stepError();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError('');
    setStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = stepError();
    if (err) {
      setError(err);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      let scheduledDate = form.scheduledDate;
      let scheduledTime = form.scheduledTime;
      if (form.venueType === 'partner_venue' && form.slot) {
        const [dateStr, timeStr] = form.slot.split('|');
        scheduledDate = dateStr;
        scheduledTime = timeStr;
      }

      const order = await createDraftOrder({
        massageType: form.massageType,
        subservices: [form.massageType],
        venueType: form.venueType,
        partnerVenue: form.venueType === 'partner_venue'
          ? { venueId: form.venueId, roomId: form.roomId }
          : undefined,
        clientLocation: form.venueType === 'client_location'
          ? { address: form.address, zone: form.zone, notes: form.notes }
          : undefined,
        scheduledDate,
        scheduledTime,
        referralSource: form.referralSource,
        previousMassageTherapy: form.previousMassageTherapy,
        onMedication: form.onMedication,
        medicationDetails: form.medicationDetails,
        exercising: form.exercising,
        exerciseFrequency: form.exerciseFrequency,
        exerciseHours: form.exerciseHours,
        healthConditions: form.healthConditions,
        disclaimerAgreed: form.disclaimerAgreed
      });

      const orderTotal = order.pricing.totalAmount;
      const paymentResponse = await axios.get(`${process.env.REACT_APP_API_URL}/payment/initialize`, {
        params: {
          amount: orderTotal * 100,
          email: user.email,
          metadata: { orderId: order._id },
          callback_url: '/marketplace-confirmation-pg'
        }
      });

      if (paymentResponse.data.status) {
        localStorage.setItem('pendingMarketplaceOrderId', order._id);
        window.location.href = paymentResponse.data.data.authorization_url;
      } else {
        setError('Could not start payment. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create order');
    } finally {
      setSubmitting(false);
    }
  };

  const seoTag = (
    <SEO
      title="Book a Massage"
      path="/book-massage"
      description="Book a verified massage therapist online in minutes. Choose your treatment, location, and schedule — payment is protected in escrow until your session is complete."
    />
  );

  if (!authLoading && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        {seoTag}
        <div className="max-w-md bg-white p-8 rounded-lg shadow text-center space-y-4">
          <h1 className="text-xl font-bold">Log in to book a massage</h1>
          <p className="text-gray-600 text-sm">
            You'll need an account so we can match you with a therapist, keep your payment protected in escrow, and let you track your booking.
          </p>
          <button
            onClick={() => navigate('/login', { state: { from: '/book-massage' } })}
            className="w-full bg-black text-white p-3 rounded-lg font-medium hover:bg-gray-800"
          >
            Log in
          </button>
          <p className="text-sm text-gray-600">
            No account? <a href="/signup" className="text-blue-600">Sign up</a>
          </p>
        </div>
      </div>
    );
  }

  if (!authLoading && user && user.kyc?.status !== 'approved') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        {seoTag}
        <div className="max-w-md bg-white p-8 rounded-lg shadow text-center space-y-4">
          <h1 className="text-xl font-bold">Verify your identity to book</h1>
          <p className="text-gray-600 text-sm">
            For your therapist's safety and yours, we require every client to submit ID verification before booking a session.
            {user.kyc?.status === 'pending' && ' Yours is currently under review — we\'ll let you know as soon as it\'s approved.'}
            {user.kyc?.status === 'rejected' && ' Your last submission was rejected — please resubmit.'}
          </p>
          <button
            onClick={() => navigate('/dashboard?tab=kyc')}
            className="w-full bg-black text-white p-3 rounded-lg font-medium hover:bg-gray-800"
          >
            {user.kyc?.status === 'pending' ? 'View verification status' : 'Submit ID verification'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      {seoTag}
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-white p-6 sm:p-8 rounded-lg shadow space-y-6">
        <h1 className="text-2xl font-bold">Book a massage</h1>

        {/* Step indicator */}
        <div className="flex items-center justify-between text-xs sm:text-sm">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1 flex items-center">
              <div className={`flex flex-col items-center flex-1 ${i <= step ? 'text-primary-dark' : 'text-gray-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold mb-1 ${
                  i <= step ? 'bg-primary-dark text-white' : 'bg-gray-200'
                }`}>
                  {i + 1}
                </div>
                <span className="hidden sm:block text-center">{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 -mt-5 ${i < step ? 'bg-primary-dark' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Massage type */}
        {step === 0 && (
          <div className="space-y-3">
            <label className="block text-sm font-medium mb-1">Choose a massage</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.map(s => (
                <button
                  type="button"
                  key={s._id}
                  onClick={() => setForm({ ...form, massageType: s.name })}
                  className={`text-left p-4 rounded-lg border ${
                    form.massageType === s.name ? 'border-primary-dark bg-accent-lighter' : 'border-gray-200'
                  }`}
                >
                  <p className="font-semibold">{s.name}</p>
                  {s.description && <p className="text-xs text-gray-500 mt-1">{s.description}</p>}
                  <p className="text-sm font-medium mt-2">₦{s.price.toLocaleString()} · {s.durationMinutes} mins</p>
                </button>
              ))}
            </div>
            {services.length === 0 && (
              <p className="text-sm text-gray-500">Loading massage options...</p>
            )}
          </div>
        )}

        {/* Step 2: Location & time */}
        {step === 1 && (
          <div className="space-y-3">
            <label className="block text-sm font-medium mb-1">Where should the session take place?</label>
            <select name="venueType" value={form.venueType} onChange={handleChange} className="w-full p-3 border rounded-lg">
              <option value="client_location">My home / office</option>
              <option value="partner_venue">A partner hotel/venue</option>
            </select>

            {form.venueType === 'client_location' ? (
              <div className="space-y-3">
                <input name="address" value={form.address} onChange={handleChange} placeholder="Address"
                  className="w-full p-3 border rounded-lg" required />
                <ZonePicker
                  mode="single"
                  value={form.zone}
                  onChange={(zoneId) => setForm(prev => ({ ...prev, zone: zoneId }))}
                />

                {checkingCoverage && <p className="text-xs text-gray-500">Checking therapist availability in this zone...</p>}
                {!checkingCoverage && coverage && !coverage.covered && (
                  <p className="text-xs bg-red-50 text-red-700 p-2 rounded-lg">
                    We don't have a verified therapist covering this zone yet, so we can't take bookings here right now. Please choose a different zone, or book a partner venue instead.
                  </p>
                )}
                {!checkingCoverage && coverage && coverage.covered && (
                  <p className="text-xs bg-green-50 text-green-700 p-2 rounded-lg">
                    {coverage.therapistCount} verified therapist{coverage.therapistCount > 1 ? 's' : ''} currently cover this zone.
                  </p>
                )}

                <input name="scheduledDate" type="date" value={form.scheduledDate} onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 border rounded-lg" required />
                <input name="scheduledTime" type="time" value={form.scheduledTime} onChange={handleChange}
                  className="w-full p-3 border rounded-lg" required />
                <textarea name="notes" value={form.notes} onChange={handleChange} rows={2}
                  placeholder="Anything else the therapist should know about your location (gate code, parking, etc.)"
                  className="w-full p-3 border rounded-lg" />
              </div>
            ) : (
              <div className="space-y-3">
                <select name="venueId" value={form.venueId} onChange={(e) => setForm({ ...form, venueId: e.target.value, roomId: '', slot: '' })}
                  className="w-full p-3 border rounded-lg" required>
                  <option value="">Select venue</option>
                  {venues.map(v => <option key={v._id} value={v._id}>{v.businessName}</option>)}
                </select>
                {selectedVenue && (
                  <select name="roomId" value={form.roomId} onChange={(e) => setForm({ ...form, roomId: e.target.value, slot: '' })}
                    className="w-full p-3 border rounded-lg" required>
                    <option value="">Select room</option>
                    {selectedVenue.rooms.filter(r => r.active).map(r => (
                      <option key={r._id} value={r._id}>{r.name} — ₦{r.pricePerSession.toLocaleString()}</option>
                    ))}
                  </select>
                )}
                {selectedRoom && (
                  <select name="slot" value={form.slot} onChange={handleChange} className="w-full p-3 border rounded-lg" required>
                    <option value="">Select available time</option>
                    {slots.map(s => (
                      <option key={s._id} value={`${s.date}|${s.time}`}>
                        {new Date(s.date).toLocaleDateString()} at {s.time}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Health & safety */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">How did you hear about us?</label>
              <select name="referralSource" value={form.referralSource} onChange={handleChange} className="w-full p-3 border rounded-lg">
                <option value="">Select one (optional)</option>
                {REFERRAL_SOURCES.map(src => <option key={src} value={src}>{src}</option>)}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="previousMassageTherapy" checked={form.previousMassageTherapy} onChange={handleChange} />
              I have had massage therapy before
            </label>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="onMedication" checked={form.onMedication} onChange={handleChange} />
              I am currently on medication
            </label>
            {form.onMedication && (
              <input name="medicationDetails" value={form.medicationDetails} onChange={handleChange}
                placeholder="Medication details" className="w-full p-3 border rounded-lg" />
            )}

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="exercising" checked={form.exercising} onChange={handleChange} />
              I exercise regularly
            </label>
            {form.exercising && (
              <div className="grid grid-cols-2 gap-3">
                <input name="exerciseFrequency" value={form.exerciseFrequency} onChange={handleChange}
                  placeholder="How often (e.g. 3x/week)" className="p-3 border rounded-lg" />
                <input name="exerciseHours" value={form.exerciseHours} onChange={handleChange}
                  placeholder="Hours per session" className="p-3 border rounded-lg" />
              </div>
            )}

            <div>
              <p className="text-sm font-medium mb-2">Do any of these apply to you? (select all that apply)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                {HEALTH_CONDITIONS.map(condition => (
                  <label key={condition} className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={form.healthConditions.includes(condition)}
                      onChange={() => toggleHealthCondition(condition)}
                    />
                    {condition}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Review & pay */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Massage</span><span className="font-medium">{form.massageType}</span></div>
              <div className="flex justify-between">
                <span>Location</span>
                <span className="font-medium">{form.venueType === 'client_location' ? 'My home/office' : selectedVenue?.businessName || 'Partner venue'}</span>
              </div>
              {form.venueType === 'client_location' ? (
                <div className="flex justify-between"><span>When</span><span className="font-medium">{form.scheduledDate} at {form.scheduledTime}</span></div>
              ) : (
                <div className="flex justify-between"><span>Room</span><span className="font-medium">{selectedRoom?.name}</span></div>
              )}
              <div className="border-t pt-2 flex justify-between text-base font-bold">
                <span>Total</span><span>₦{totalAmount.toLocaleString()}</span>
              </div>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" name="disclaimerAgreed" checked={form.disclaimerAgreed} onChange={handleChange} required />
              I confirm the health & safety information I provided is accurate and agree to the health disclaimer.
            </label>
          </div>
        )}

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <div className="flex gap-3">
          {step > 0 && (
            <button type="button" onClick={goBack}
              className="flex-1 border border-gray-300 text-gray-700 p-3 rounded-lg font-medium hover:bg-gray-50">
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={goNext}
              className="flex-1 bg-black text-white p-3 rounded-lg font-medium hover:bg-gray-800">
              Continue
            </button>
          ) : (
            <button type="submit" disabled={submitting}
              className="flex-1 bg-black text-white p-3 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50">
              {submitting ? 'Processing...' : `Pay ₦${totalAmount.toLocaleString()}`}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default BookMassagePage;
